import type {
  AgentEvent,
  ChatAccepted,
  ChatRequest,
  ImportStatus,
  ImportStepName,
  Rating,
  ToolStepStatus,
} from './types';
import { toPack } from './crewWire';
import { toPricingUpdate } from './pricingWire';
import { scalar, str } from './wireValues';

// With crewWire.ts (the crew's card bodies, Plan 4a), the only files that know
// odin-agents' wire field names: the "Portal contract" in Plan 3a, which the
// server owns. If the server's shapes change, edit these files and their tests;
// nothing else in the feature changes.
//
// What toAgentEvent returns:
// - null for an unknown event type, bad JSON, or a payload that isn't a JSON
//   object, so events added by later plans can't break the panel;
// - null for a text, tool_step, citation or import_progress event missing the
//   field the panel can't render without (delta; callId, name and a known
//   status; n and factId; a known step and status);
// - otherwise an event, with a default filled in for any optional field that is
//   missing or the wrong type. error, done, crew_progress and approval_card have
//   no required fields: a sparse error still ends the turn with a message, and a
//   done with a missing or unrecognised status counts as completed.

/** Shown when an error event carries no message. Reused wherever the panel needs the same fallback. */
export const GENERIC_FAILURE = 'Something went wrong. Try again.';

const IMPORT_STEPS: readonly ImportStepName[] = [
  'read',
  'extract',
  'check',
  'compare',
];
const IMPORT_STATUSES: readonly ImportStatus[] = ['started', 'done', 'failed'];

const TOOL_STATUSES: readonly ToolStepStatus[] = ['started', 'done', 'error'];

// str and scalar live in wireValues.ts, shared with crewWire.ts.

function parseObject(data: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(data);
    return typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function toAgentEvent(type: string, data: string): AgentEvent | null {
  const d = parseObject(data);
  if (!d) return null;
  switch (type) {
    case 'text': {
      const delta = str(d.delta);
      if (delta === undefined) return null;
      return { type: 'text', agent: str(d.agent) ?? 'assistant', delta };
    }
    case 'tool_step': {
      const callId = str(d.callId);
      const name = str(d.name);
      const status = TOOL_STATUSES.find((s) => s === d.status);
      if (callId === undefined || name === undefined || status === undefined)
        return null;
      return {
        type: 'tool_step',
        agent: str(d.agent) ?? 'assistant',
        callId,
        name,
        status,
        summary: str(d.summary) ?? '',
      };
    }
    case 'citation': {
      const n = d.n;
      const factId = str(d.factId);
      if (typeof n !== 'number' || factId === undefined) return null;
      return {
        type: 'citation',
        citation: {
          n,
          factId,
          label: str(d.label) ?? factId,
          value: scalar(d.value),
          source: str(d.source) ?? '',
          tool: str(d.tool) ?? '',
        },
      };
    }
    case 'draft_skills': {
      const skills = Array.isArray(d.skills)
        ? d.skills.filter((s): s is string => typeof s === 'string')
        : [];
      // Nothing to show without a name; a mixed list is malformed.
      if (
        !Array.isArray(d.skills) ||
        skills.length === 0 ||
        skills.length !== d.skills.length
      )
        return null;
      return { type: 'draft_skills', agent: str(d.agent) ?? '', skills };
    }
    case 'crew_progress':
      return {
        type: 'crew_progress',
        stage: str(d.stage) ?? '',
        round: typeof d.round === 'number' ? d.round : 0,
        status: str(d.status) ?? '',
        detail: str(d.detail) ?? null,
      };
    case 'import_progress': {
      const step = IMPORT_STEPS.find((s) => s === d.step);
      const status = IMPORT_STATUSES.find((s) => s === d.status);
      if (step === undefined || status === undefined) return null;
      return {
        type: 'import_progress',
        step,
        status,
        detail: str(d.detail) ?? null,
      };
    }
    case 'approval_card': {
      const actions = Array.isArray(d.actions) ? d.actions : [];
      const kind = str(d.kind) ?? '';
      const pricing =
        kind === 'pricing_update' ? toPricingUpdate(d.body) : null;
      return {
        type: 'approval_card',
        kind,
        cardId: str(d.cardId) ?? '',
        title: str(d.title) ?? '',
        actions: actions.filter((a): a is string => typeof a === 'string'),
        // The pack body has its own snake_case names; crewWire.ts reads them.
        pack: kind === 'pack' ? toPack(d.body) : null,
        ...(pricing ? { pricing } : {}),
      };
    }
    case 'error':
      return {
        type: 'error',
        code: str(d.code) ?? 'internal',
        message: str(d.message) ?? GENERIC_FAILURE,
        runId: str(d.runId) ?? '',
      };
    case 'done':
      return {
        type: 'done',
        status: d.status === 'failed' ? 'failed' : 'completed',
      };
    default:
      return null;
  }
}

/** runId and sessionId from the 202 reply to POST /chat (eventsUrl is derived from runId). */
export function toChatAccepted(body: unknown): ChatAccepted | null {
  if (typeof body !== 'object' || body === null) return null;
  const { runId, sessionId } = body as Record<string, unknown>;
  return typeof runId === 'string' &&
    runId !== '' &&
    typeof sessionId === 'string' &&
    sessionId !== ''
    ? { runId, sessionId }
    : null;
}

/** POST /chat body. The server rejects unknown fields, so send exactly these. */
export const toChatBody = (request: ChatRequest) => ({
  message: request.message,
  // The assistant doesn't know lenders yet: a lender page sends no record.
  recordRef:
    request.recordRef?.type === 'assessment' ? request.recordRef : null,
  ...(request.sessionId ? { sessionId: request.sessionId } : {}),
});

/** POST /runs/{runId}/feedback body. */
export const toFeedbackBody = (rating: Rating) => ({ rating });
